import { computed, ref, type ComputedRef, type Ref } from 'vue'

/**
 * Lets the APP offer to install itself.
 *
 * Chrome hands us its own prompt through `beforeinstallprompt` when it feels
 * like it, and that used to be the only way the button appeared. It is not
 * enough: every iOS browser fires that event NEVER, Chrome withholds it once
 * the app looks installed, and an Android phone can simply not fire it. The
 * offer disappearing in all those cases left no way in at all.
 *
 * So the button is shown whenever the app is running in a browser, and the
 * event is treated as a shortcut rather than a precondition: with it, one tap
 * installs; without it, the person is told where their browser keeps the
 * command.
 */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallPlatform = 'ios' | 'android' | 'desktop'

const deferred = ref<InstallPromptEvent | null>(null)
const standalone = ref(false)
const showSteps = ref(false)

/** Already installed and launched from the home screen: nothing left to offer. */
const runningStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  window.matchMedia?.('(display-mode: minimal-ui)').matches === true ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true

const detectPlatform = (): InstallPlatform => {
  const agent = window.navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(agent)) {
    return 'ios'
  }
  return /Android/i.test(agent) ? 'android' : 'desktop'
}

export const listenForInstallPrompt = (): void => {
  standalone.value = runningStandalone()

  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own mini-infobar as well, and the person
    // is offered the same thing twice in two different voices.
    event.preventDefault()
    deferred.value = event as InstallPromptEvent
  })

  window.addEventListener('appinstalled', () => {
    deferred.value = null
    standalone.value = true
    showSteps.value = false
  })
}

export const useInstallPrompt = (): {
  canInstall: ComputedRef<boolean>
  platform: ComputedRef<InstallPlatform>
  showSteps: Ref<boolean>
  install: () => Promise<void>
} => {
  const install = async (): Promise<void> => {
    const event = deferred.value
    if (!event) {
      // No shortcut from the browser — hand over the manual route instead of
      // a button that does nothing.
      showSteps.value = true
      return
    }

    // A prompt can only be shown once, so it is dropped whatever the answer:
    // Chrome refuses a second call on the same event.
    deferred.value = null
    await event.prompt()
    const { outcome } = await event.userChoice
    if (outcome === 'accepted') {
      standalone.value = true
    }
  }

  return {
    canInstall: computed(() => !standalone.value),
    platform: computed(() => detectPlatform()),
    showSteps,
    install,
  }
}
